'use client';

import { lazy, useMemo, useState } from 'react';
import { useBenzingaEdge } from '@benzinga/edge';
import { Pagination } from '@benzinga/core-ui';
import { AiOutlineUnlock } from 'react-icons/ai';
import { MemberCard } from '../../../../src/components/GovTrades';
import {
  filterMembersList,
  MEMBERS_SORT_OPTIONS,
  sortMembersList,
  type MembersChamberFilter,
  type MembersListItem,
  type MembersPartyFilter,
  type MembersSortKey,
  type MembersSortOrder,
} from './utils';

const BzEdgeCTA = lazy(() => import('@benzinga/ads').then(module => ({ default: module.BzEdgeCTA })));

const PAGE_SIZE = 10;
const EDGE_FREE_PAGES = 2;

const CHAMBER_OPTIONS: { value: MembersChamberFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'house', label: 'House' },
  { value: 'senate', label: 'Senate' },
];

const PARTY_OPTIONS: { value: MembersPartyFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'D', label: 'Democrat' },
  { value: 'R', label: 'Republican' },
  { value: 'I', label: 'Independent' },
];

export const MembersGrid = ({ members }: { members: MembersListItem[] }) => {
  const { hasEdgeSubscription: hasPermission } = useBenzingaEdge();
  const [page, setPage] = useState(1);
  const [chamber, setChamber] = useState<MembersChamberFilter>('all');
  const [party, setParty] = useState<MembersPartyFilter>('all');
  const [sortKey, setSortKey] = useState<MembersSortKey>('date_of_last_recorded_trade');
  const [sortOrder, setSortOrder] = useState<MembersSortOrder>('desc');

  const filteredSorted = useMemo(() => {
    const filtered = filterMembersList(members ?? [], chamber, party);
    return sortMembersList(filtered, sortKey, sortOrder);
  }, [members, chamber, party, sortKey, sortOrder]);

  const pageMembers = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredSorted.slice(start, start + PAGE_SIZE);
  }, [filteredSorted, page]);

  const locked = !hasPermission;
  const showEdgeWall = locked && page > EDGE_FREE_PAGES;

  const resetPage = () => setPage(1);

  const handleChamber = (value: MembersChamberFilter) => {
    setChamber(value);
    resetPage();
  };

  const handleParty = (value: MembersPartyFilter) => {
    setParty(value);
    resetPage();
  };

  const handleSort = (value: string) => {
    const [key, order] = value.split('+') as [MembersSortKey, MembersSortOrder];
    setSortKey(key);
    setSortOrder(order);
    resetPage();
  };

  const handlePageChange = ({ nextPage }: { nextPage: number }) => {
    setPage(nextPage);
  };

  return (
    <div className="members-grid-wrapper">
      <div className="members-sec-head">
        <h2 className="members-sec-head__title">
          Congress Members{' '}
          <span className="members-sec-head__count">
            ({filteredSorted.length.toLocaleString()} active traders)
          </span>
        </h2>
        <div className={`members-filters${locked ? ' members-filters--locked' : ''}`}>
          <label className="members-filter">
            <span>Chamber</span>
            <select
              aria-label="Filter by chamber"
              disabled={locked}
              onChange={e => handleChamber(e.target.value as MembersChamberFilter)}
              value={chamber}
            >
              {CHAMBER_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="members-filter">
            <span>Party</span>
            <select
              aria-label="Filter by party"
              disabled={locked}
              onChange={e => handleParty(e.target.value as MembersPartyFilter)}
              value={party}
            >
              {PARTY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>

          <label className="members-filter">
            <span>Sort</span>
            <select
              aria-label="Sort members"
              disabled={locked}
              onChange={e => handleSort(e.target.value)}
              value={`${sortKey}+${sortOrder}`}
            >
              {MEMBERS_SORT_OPTIONS.map(opt => (
                <option key={`${opt.key}+${opt.order}`} value={`${opt.key}+${opt.order}`}>
                  {opt.name}
                </option>
              ))}
            </select>
          </label>

          {locked ? (
            <a
              className="members-filters__unlock"
              href="https://www.benzinga.com/premium/ideas/benzinga-edge-checkout/?t=be8be9we4gewe1be11&utm_source=government-trades"
              target="_blank"
              rel="noreferrer"
            >
              <AiOutlineUnlock size={16} /> Unlock with Benzinga Edge
            </a>
          ) : null}
        </div>
      </div>

      {filteredSorted.length === 0 ? (
        <p className="members-empty">No members match these filters.</p>
      ) : (
        <>
          <div className="members-mgrid relative">
            {pageMembers.map(member => (
              <MemberCard key={member.filer_info?.member_id ?? member.id} {...member} />
            ))}
            {showEdgeWall ? (
              <div className="members-edge-wall">
                <BzEdgeCTA type="gov-trades" />
              </div>
            ) : null}
          </div>

          <div className="members-pagination">
            <Pagination
              buttonClassName="pagination-button"
              defaultPage={page}
              onPageChanged={handlePageChange}
              pageSize={PAGE_SIZE}
              totalItems={filteredSorted.length}
            />
          </div>
        </>
      )}
    </div>
  );
};
